import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaPdpCopyEvent } from "@stoneage/App/Utilities/DataLayer";
import IconLink from "@stoneage/XDL/Icons/IconLink";
import IconPrint from "@stoneage/XDL/Icons/IconPrint";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    customerNumber: getBillToCustomerNumber(state),
    userEmail: state.context.session?.email,
    impersonation: trackAsImpersonation(state),
});

export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    printLink?: LinkPresentationProps;
    copyLink?: LinkPresentationProps;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    printLink: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: IconPrint,
                size: 12,
                css: css`
                    margin-right: 3px;
                `,
            },
        },
        css: css`
            margin-left: 24px;
            &:hover > * {
                color: ${getColor("primary")} !important;
            }
        `,
    },
    copyLink: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: IconLink,
                size: 12,
                css: css`
                    margin-right: 3px;
                `,
            },
        },
        css: css`
            margin-left: 24px;
            &:hover > * {
                color: ${getColor("primary")} !important;
            }
        `,
    },
};

const styles = shareLinkStyles;

const ProductDetailsShareLink: React.FC<Props & HasToasterContext> = ({
    product,
    toaster,
    customerNumber,
    userEmail,
    impersonation,
}) => {
    if (!product) {
        return null;
    }

    const copyLinkToClipboard = () => {
        const dummy = document.createElement("input");
        const text = window.location.href;

        document.body.appendChild(dummy);
        dummy.value = text;
        dummy.select();
        document.execCommand("copy");
        document.body.removeChild(dummy);
        toaster.addToast({
            body: translate("Link Copied!"),
            messageType: "info",
        });

        // XNG-Changes: Track GA4
        trackGaPdpCopyEvent(
            {
                productTitle: product.productTitle,
                productNumber: product.productNumber,
            },
            impersonation,
            userEmail,
            customerNumber,
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductShareLink extendedStyles={styles.link} />
            <Link onClick={() => window.print()} {...styles.printLink} id="printpdp">
                {translate("Print")}
            </Link>
            <Link onClick={copyLinkToClipboard} {...styles.copyLink}>
                {translate("Copy Link")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(withToaster(ProductDetailsShareLink))),
    definition: {
        displayName: "Share, Print & Copy Links",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
